import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { useStateContext } from '../../contexts/ContextProvider';
import { isTemplateLibraryMode } from '../../helpers/templateLibrary';
import { useSwitchToFacility } from './useSwitchToFacility';
import { useSuperAdminMode } from './navigation/useSuperAdminMode';
import { LIBRARY_RETURN_PATH_KEY } from '../../pages/super-admin/templates/useOpenInLibrary';

const LIBRARY_HOME = '/super-admin/flow-templates';

/** Éditeurs d'un modèle : le bandeau ne s'affiche que pendant la modification. */
const TEMPLATE_EDITOR_ROUTES = ['/flows/update/:id', '/email-templates/:id', '/surveys/list/update/:id'];

const isTemplateEditor = (pathname: string) => TEMPLATE_EDITOR_ROUTES.some((pattern) => {
    const match = matchPath(pattern, pathname);
    return Boolean(match?.params.id && /^\d+$/.test(match.params.id));
});

/**
 * Bandeau du « mode bibliothèque » : un super admin consulte l'hôtel qui porte
 * la bibliothèque officielle de modèles. Tout ce qui s'y enregistre peut être
 * proposé à tous les hôtels : on le dit, et on offre le retour à son hôtel et
 * à la page du back-office d'où l'on vient.
 *
 * Affiché seulement pendant la modification d'un modèle (éditeur de flow,
 * d'e-mail ou de formulaire) : ailleurs sur l'hôtel bibliothèque, et en
 * back-office, il gênait sans rien apprendre.
 */
export default function LibraryModeBanner() {
    const { t } = useTranslation('navigation');
    const { user, currentFacility } = useStateContext();
    const { switchTo, pendingId } = useSwitchToFacility();
    const navigate = useNavigate();
    const { isOn: isBackOffice } = useSuperAdminMode();
    const { pathname } = useLocation();

    if (isBackOffice || !isTemplateEditor(pathname) || !isTemplateLibraryMode(user, currentFacility)) return null;

    const homeFacilityId = user?.facility_id ?? null;
    const canReturn = Boolean(homeFacilityId) && homeFacilityId !== currentFacility?.id;

    const readReturnPath = () => {
        try {
            return sessionStorage.getItem(LIBRARY_RETURN_PATH_KEY) || LIBRARY_HOME;
        } catch {
            return LIBRARY_HOME;
        }
    };

    const handleReturn = async () => {
        if (!homeFacilityId) return;
        const path = readReturnPath();
        if (await switchTo(homeFacilityId)) {
            try {
                sessionStorage.removeItem(LIBRARY_RETURN_PATH_KEY);
            } catch {
                // Stockage indisponible : rien à nettoyer.
            }
            navigate(path);
        }
    };

    return (
        <div role="status" className="border-b border-main-color/20 bg-main-color-light px-4 py-2.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-main-color-dark">
                <BookOpenIcon className="h-5 w-5 flex-shrink-0 text-main-color" aria-hidden="true" />
                <span className="flex-1">
                    {t('library_banner.message', { name: currentFacility?.name ?? '' })}
                </span>
                <Link to={readReturnPath()} className="text-sm font-medium text-main-color hover:underline">
                    {t('library_banner.open_library')}
                </Link>
                {canReturn && (
                    <button
                        type="button"
                        onClick={handleReturn}
                        disabled={pendingId !== null}
                        className="rounded-lg bg-main-color px-3 py-1.5 text-sm font-semibold text-white hover:bg-main-color-dark disabled:opacity-50"
                    >
                        {t('library_banner.return', { name: user?.facility?.name ?? '' })}
                    </button>
                )}
            </div>
        </div>
    );
}
